import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { InstructionsPanel } from './InstructionsPanel.js';
import { ChatPreview } from '../components/ChatPreview.js';
import { Panel } from '../components/Panel.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ColorField,
  ErrorNote,
  Field,
  FileField,
  Icon,
  Modal,
  PageHeader,
  PagedTableShell,
  Select,
  Stat,
  Table,
  Tabs,
  Td,
  TextInput,
  Textarea,
  buttonClass,
  useClientPaging,
} from '../components/ui/index.js';
import { apiRequest, openAuthenticatedPdf } from '../lib/api.js';
import { downloadFile, readJson, useRemote } from '../lib/remote.js';
import { useAdminLocale } from '../lib/admin-locale.js';
import { useUiPrefs } from '../lib/ui-prefs.js';

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  const body: unknown = await response.json().catch(() => null);
  if (body && typeof body === 'object' && 'error' in body) {
    const message = (body as { error?: { message?: string } }).error?.message;
    if (message) return message;
  }
  return fallback;
}

async function loadAssetObjectUrl(path: string): Promise<string | null> {
  const response = await apiRequest(path);
  if (!response.ok) return null;
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

interface SiteLocale {
  locale: string;
  enabled: boolean;
}

interface SiteRow {
  id: string;
  name: string;
  canonicalOrigin: string;
  status: string;
  tenant?: { id: string; name: string } | undefined;
  locales?: SiteLocale[] | undefined;
  connection?: 'connected' | 'revoked' | 'none' | undefined;
  lastSyncAt?: string | null | undefined;
  lastSyncStatus?: string | null | undefined;
}

type Connection = 'connected' | 'revoked' | 'none';

const CONNECTION_BADGE: Record<Connection, string> = {
  connected: 'ACTIVE',
  revoked: 'FAILED',
  none: 'PENDING',
};

const linkClass = 'font-medium text-blue-600 hover:text-blue-800 dark:hover:text-blue-200';

function formatTime(value: string | null | undefined, locale: 'el' | 'en'): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString(locale === 'en' ? 'en-GB' : 'el-GR', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
}

function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

function connectionOf(site: SiteRow): Connection {
  return site.connection ?? 'none';
}

function connectionTone(connection: Connection): string {
  if (connection === 'connected')
    return 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300';
  if (connection === 'revoked') return 'bg-rose-50 dark:bg-rose-950/40 text-rose-600';
  return 'bg-amber-50 dark:bg-amber-950/40 text-amber-700';
}

function relativeTime(
  value: string | null | undefined,
  text: (el: string, en: string) => string,
  locale: 'el' | 'en',
): string {
  if (!value) return text('Δεν έχει συγχρονιστεί', 'Never synced');
  const date = new Date(value);
  const delta = Date.now() - date.getTime();
  if (Number.isNaN(delta)) return '—';
  const minutes = Math.round(delta / 60_000);
  if (minutes < 1) return text('μόλις τώρα', 'just now');
  if (minutes < 60)
    return minutes === 1
      ? text('πριν 1 λεπτό', '1 minute ago')
      : text(`πριν ${minutes} λεπτά`, `${minutes} minutes ago`);
  const hours = Math.round(minutes / 60);
  if (hours < 24)
    return hours === 1
      ? text('πριν 1 ώρα', '1 hour ago')
      : text(`πριν ${hours} ώρες`, `${hours} hours ago`);
  const days = Math.round(hours / 24);
  if (days < 30)
    return days === 1
      ? text('πριν 1 ημέρα', '1 day ago')
      : text(`πριν ${days} ημέρες`, `${days} days ago`);
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'el-GR');
}

export function SitesPage(props: {
  canWrite: boolean;
  platform: boolean;
  tenantName: string | null;
  onOpenSite: (tenantId: string, siteId: string) => void;
}) {
  const { text, locale } = useAdminLocale();
  const navigate = useNavigate();
  const remote = useRemote<SiteRow[]>(
    props.platform ? '/v1/platform/sites' : '/v1/sites',
    props.platform ? null : undefined,
  );
  const rows = remote.data ?? [];
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [connectionFilter, setConnectionFilter] = useState<'all' | Connection>('all');
  const tenants = [
    ...new Map(
      rows.flatMap((site) => (site.tenant ? [[site.tenant.id, site.tenant.name] as const] : [])),
    ).entries(),
  ];
  const visible = rows.filter((site) => {
    const haystack =
      `${site.name} ${site.canonicalOrigin} ${site.tenant?.name ?? ''}`.toLowerCase();
    if (query.trim() && !haystack.includes(query.trim().toLowerCase())) return false;
    if (tenantFilter !== 'all' && site.tenant?.id !== tenantFilter) return false;
    if (connectionFilter !== 'all' && connectionOf(site) !== connectionFilter) return false;
    return true;
  });
  const paging = useClientPaging(visible, {
    resetKey: `${query}|${tenantFilter}|${connectionFilter}`,
  });
  const connected = rows.filter((site) => connectionOf(site) === 'connected').length;
  const attention = rows.length - connected;

  function manage(site: SiteRow): void {
    if (site.tenant && (props.platform || site.tenant.id)) {
      props.onOpenSite(site.tenant.id, site.id);
      return;
    }
    void navigate(`/sites/${site.id}`);
  }

  return (
    <section className="admin-page">
      <PageHeader
        description={
          props.platform
            ? text(
                'Ιστοσελίδες και chatbot όλων των πελατών. Η προσθήκη γίνεται στον επιλεγμένο πελάτη.',
                'Websites and chatbots for all customers. New websites are added to the selected customer.',
              )
            : text(
                'Οι ιστοσελίδες του οργανισμού σας. Κάθε μία συνδέεται με ένα WordPress μέσω του πρόσθετου.',
                "Your organization's websites. Each one connects to WordPress through the plugin.",
              )
        }
        actions={
          props.canWrite ? (
            <Button type="button" onClick={() => setCreating((value) => !value)}>
              <Icon name="plus" className="size-4" /> {text('Προσθήκη ιστοσελίδας', 'Add website')}
            </Button>
          ) : null
        }
      />
      {creating ? (
        <CreateSiteForm
          tenantName={props.tenantName}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            void remote.mutate();
            void navigate(`/sites/${id}`);
          }}
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat
          icon="globe"
          label={text('Ιστοσελίδες', 'Websites')}
          value={remote.data ? rows.length : '—'}
        />
        <Stat
          icon="link"
          label={text('Συνδεδεμένες', 'Connected')}
          value={remote.data ? connected : '—'}
        />
        <Stat
          icon="alert"
          label={text('Χρειάζονται έλεγχο', 'Need attention')}
          value={remote.data ? attention : '—'}
          hint={text('Χωρίς ενεργή σύνδεση πρόσθετου', 'No active plugin connection')}
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-nowrap sm:items-center">
        <TextInput
          flush
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={text('Αναζήτηση ιστοσελίδας ή πελάτη…', 'Search website or customer…')}
          aria-label={text('Αναζήτηση ιστοσελίδας', 'Search websites')}
          className="w-full sm:w-auto sm:max-w-sm sm:flex-1 sm:basis-64"
        />
        {tenants.length > 1 ? (
          <Select
            aria-label={text('Πελάτης', 'Customer')}
            value={tenantFilter}
            onChange={setTenantFilter}
            className="w-full shrink-0 sm:w-56"
            options={[
              { value: 'all', label: text('Όλοι οι πελάτες', 'All customers') },
              ...tenants.map(([id, name]) => ({ value: id, label: name })),
            ]}
          />
        ) : null}
        <Select
          aria-label={text('Κατάσταση σύνδεσης', 'Connection status')}
          value={connectionFilter}
          onChange={(next) => setConnectionFilter(next as typeof connectionFilter)}
          className="w-full shrink-0 sm:w-56"
          options={[
            { value: 'all', label: text('Όλες οι καταστάσεις', 'All statuses') },
            { value: 'connected', label: text('Συνδεδεμένο', 'Connected') },
            { value: 'none', label: text('Χωρίς σύνδεση', 'Not connected') },
            { value: 'revoked', label: text('Ελέγξτε σύνδεση', 'Check connection') },
          ]}
        />
      </div>

      <Panel
        loading={!remote.data && !remote.error}
        error={remote.error ? remote.error.message : null}
        empty={visible.length === 0}
        emptyLabel={text(
          'Δεν υπάρχουν ιστοσελίδες σε αυτή την προβολή.',
          'There are no websites in this view.',
        )}
        emptyHint={
          props.canWrite
            ? text(
                'Η προσθήκη καταχωρίζει το domain. Έπειτα εκδίδεται κωδικός και συνδέεται το πρόσθετο.',
                'Adding a website registers its domain. Then issue a code and connect the plugin.',
              )
            : text(
                'Ο διαχειριστής του οργανισμού δημιουργεί ιστοσελίδες.',
                'The organization administrator creates websites.',
              )
        }
      >
        <Card flush>
          <PagedTableShell pagination={paging.total > 0 ? paging.pager : null}>
            <Table
              head={[
                text('Ιστοσελίδα', 'Website'),
                text('Πελάτης', 'Customer'),
                text('Σύνδεση', 'Connection'),
                text('Γλώσσες', 'Languages'),
                text('Συγχρονισμός', 'Sync'),
                '',
              ]}
            >
              {paging.rows.map((site) => {
                const connection = connectionOf(site);
                return (
                  <tr
                    key={site.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/70 dark:hover:bg-slate-800/70"
                  >
                    <Td>
                      <div className="flex items-center gap-3">
                        <span
                          className={`grid size-9 shrink-0 place-items-center rounded-full text-sm font-semibold ${connectionTone(connection)}`}
                        >
                          {site.name.trim().charAt(0).toUpperCase() || 'W'}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-medium text-slate-900 dark:text-slate-100">
                            {hostOf(site.canonicalOrigin)}
                          </span>
                          <span className="block truncate text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                            {site.canonicalOrigin}
                          </span>
                        </span>
                      </div>
                    </Td>
                    <Td muted>{site.tenant?.name ?? props.tenantName ?? '—'}</Td>
                    <Td>
                      <Badge
                        value={CONNECTION_BADGE[connection]}
                        label={
                          {
                            connected: text('Συνδεδεμένο', 'Connected'),
                            revoked: text('Ελέγξτε σύνδεση', 'Check connection'),
                            none: text('Χωρίς σύνδεση', 'Not connected'),
                          }[connection]
                        }
                      />
                    </Td>
                    <Td>
                      {site.locales && site.locales.length > 0 ? (
                        <span className="flex flex-wrap gap-1">
                          {site.locales.map((item) => (
                            <span
                              key={item.locale}
                              className={
                                item.enabled
                                  ? 'rounded-md bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300'
                                  : 'rounded-md px-1.5 py-0.5 text-xs text-slate-300'
                              }
                            >
                              {item.locale.toUpperCase()}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500">—</span>
                      )}
                    </Td>
                    <Td muted>
                      {relativeTime(site.lastSyncAt, text, locale)}
                      {site.lastSyncStatus === 'FAILED' ? (
                        <span className="mt-0.5 block text-xs text-rose-700 dark:text-rose-300">
                          {text('Απέτυχε', 'Failed')}
                        </span>
                      ) : null}
                    </Td>
                    <Td className="text-right">
                      <Button type="button" variant="secondary" onClick={() => manage(site)}>
                        <Icon name="arrow" className="size-4" /> {text('Διαχείριση', 'Manage')}
                      </Button>
                    </Td>
                  </tr>
                );
              })}
            </Table>
          </PagedTableShell>
        </Card>
      </Panel>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50 dark:bg-blue-950/40 px-4 py-3">
        <span className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
          <Icon name="globe" className="mt-0.5 size-4 shrink-0 text-blue-600" />
          <span>
            <span className="block font-medium text-slate-900 dark:text-slate-100">
              {text('Σύνδεση νέου WordPress', 'Connect a new WordPress site')}
            </span>
            {text(
              'Καταχώριση domain, εγκατάσταση του πρόσθετου και ολοκλήρωση με τον κωδικό σύνδεσης.',
              'Register the domain, install the plugin, and complete setup with the connection code.',
            )}
          </span>
        </span>
        {props.canWrite ? (
          <Button type="button" variant="secondary" onClick={() => setCreating(true)}>
            <Icon name="plus" className="size-4" /> {text('Προσθήκη ιστοσελίδας', 'Add website')}
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function CreateSiteForm(props: {
  tenantName: string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { text } = useAdminLocale();
  const [name, setName] = useState('');
  const [origin, setOrigin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  return (
    <Modal
      title={text('Προσθήκη website', 'Add website')}
      description={text(
        'Σύνδεση ενός WordPress website. Η γλώσσα ξεκινά στα Ελληνικά. English και Français ενεργοποιούνται αφού υπάρχει χαιρετισμός.',
        'Connect a WordPress website. The initial language is Greek. English and French are enabled after a greeting is added.',
      )}
      onClose={props.onClose}
    >
      <ol className="mb-4 grid gap-2 text-sm text-slate-600 dark:text-slate-400 dark:text-slate-500 sm:grid-cols-3">
        <li className="rounded-lg bg-blue-50 dark:bg-blue-950/40 px-3 py-2 font-medium text-blue-800">
          {text('1. Στοιχεία website', '1. Website details')}
        </li>
        <li className="rounded-lg bg-slate-50 dark:bg-slate-950 px-3 py-2">
          {text('2. Κωδικός εγκατάστασης', '2. Installation code')}
        </li>
        <li className="rounded-lg bg-slate-50 dark:bg-slate-950 px-3 py-2">
          {text('3. Σύνδεση plugin και επιλογή σελίδων', '3. Connect plugin and select pages')}
        </li>
      </ol>
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          setPending(true);
          setError(null);
          void readJson<{ id: string }>('/v1/sites', {
            method: 'POST',
            body: JSON.stringify({ name, canonicalOrigin: origin }),
          })
            .then((created) => props.onCreated(created.id))
            .catch((caught: unknown) => {
              setError(
                caught instanceof Error
                  ? caught.message
                  : text('Η δημιουργία απέτυχε', 'Creation failed'),
              );
            })
            .finally(() => setPending(false));
        }}
      >
        <Field label={text('Πελάτης', 'Customer')}>
          <TextInput
            value={props.tenantName ?? text('Επιλεγμένος πελάτης', 'Selected customer')}
            readOnly
          />
        </Field>
        <Field label={text('Όνομα website', 'Website name')}>
          <TextInput required value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field
          label="Domain"
          hint={text(
            'Ακριβές origin, χωρίς path. Το www είναι διαφορετικό domain.',
            'Exact origin without a path. The www variant is a different domain.',
          )}
        >
          <TextInput
            required
            type="url"
            placeholder="https://example.gr"
            value={origin}
            onChange={(event) => setOrigin(event.target.value)}
          />
        </Field>
        <Field label={text('Τύπος', 'Type')}>
          <Select
            className="mt-1.5"
            value="wordpress"
            disabled
            onChange={() => undefined}
            options={[{ value: 'wordpress', label: 'WordPress' }]}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            <Icon name="x" className="size-4" /> {text('Άκυρο', 'Cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            <Icon name="arrow" className="size-4" />
            {pending
              ? text('Δημιουργία…', 'Creating…')
              : text('Δημιουργία και συνέχεια', 'Create and continue')}
          </Button>
          {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
        </div>
      </form>
    </Modal>
  );
}

interface BotData {
  name: string;
  enabled: boolean;
  defaultLocale: string;
  locales: Array<{
    locale: string;
    greeting: string;
    fallback: string;
    enabled: boolean;
    starterQuestions?: StarterQuestion[];
  }>;
  settings: {
    privacyUrl: string | null;
    contactUrl: string | null;
    leadNotifyEmail: string | null;
    leadNotifyEmailPending?: string | null;
    noticeVersion: string;
    logoAssetId?: string | null;
    colors?: unknown;
    position?: string | null;
    businessInstructions?: string | null;
  };
}

interface StarterQuestion {
  question: string;
  answer: string;
  icon: StarterIcon;
  action: StarterAction;
}

type StarterIcon = 'QUESTION' | 'INFO' | 'SERVICES' | 'PRICE' | 'CLOCK' | 'CONTACT';
type StarterAction = 'ANSWER' | 'CONTACT' | 'QUOTE';

interface Appearance {
  name: string;
  greeting: string;
  color: string;
  position: 'left' | 'right';
  locales: string[];
}

function primaryColor(settings: BotData['settings']): string {
  const colors = settings.colors;
  if (colors && typeof colors === 'object' && !Array.isArray(colors) && 'primary' in colors) {
    const value = colors.primary;
    if (typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value)) return value.toUpperCase();
  }
  return '#2563EB';
}

function appearanceFrom(bot: BotData): Appearance {
  const current =
    bot.locales.find((locale) => locale.locale === bot.defaultLocale) ?? bot.locales[0];
  return {
    name: bot.name,
    greeting: current?.greeting ?? '',
    color: primaryColor(bot.settings),
    position: bot.settings.position === 'left' ? 'left' : 'right',
    locales: activeLocaleCodes(bot),
  };
}

export function SiteDetailPage(props: { canWrite: boolean }) {
  const { text } = useAdminLocale();
  const { siteId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const sectionParam = params.get('section');
  const section =
    sectionParam === 'appearance' || sectionParam === 'instructions' || sectionParam === 'danger'
      ? sectionParam
      : 'general';
  const site = useRemote<SiteRow & { lastHeartbeatAt?: string | null }>(`/v1/sites/${siteId}`);
  const bot = useRemote<BotData>(`/v1/sites/${siteId}/bot`);
  const sources = useRemote<
    Array<{
      id: string;
      title: string;
      type: string;
      status: string;
      locale: string | null;
      latestVersionStatus: string | null;
    }>
  >(`/v1/sites/${siteId}/sources`);
  const syncs = useRemote<
    Array<{
      id: string;
      status: string;
      finishedAt: string | null;
      createdAt?: string;
      errorCode: string | null;
      counters?: { accepted: number; rejected: number; duplicate: number; total: number };
      progress?: { done: number; total: number; percent: number };
      failedItems?: Array<{
        id: string;
        externalId: string;
        revision: number;
        status: string;
        errorCode: string | null;
      }>;
    }>
  >(`/v1/sites/${siteId}/sync-runs`);
  const [previewOpen, setPreviewOpen] = useState(false);
  const sourceRows = sources.data ?? [];
  const sourcePaging = useClientPaging(sourceRows, { resetKey: siteId });
  const tabItems = [
    { id: 'general', label: text('Γενικά', 'General'), icon: 'settings' as const },
    { id: 'appearance', label: text('Εμφάνιση', 'Appearance'), icon: 'eye' as const },
    {
      id: 'instructions',
      label: text('Οδηγίες Βοηθού', 'Assistant instructions'),
      icon: 'file' as const,
    },
    ...(props.canWrite
      ? [
          {
            id: 'danger',
            label: text('Διαγραφή', 'Delete'),
            icon: 'trash' as const,
            tone: 'danger' as const,
          },
        ]
      : []),
  ];

  return (
    <section className="admin-page">
      <PageHeader
        title={site.data?.name ?? text('Ιστοσελίδα', 'Website')}
        description={site.data?.canonicalOrigin}
        badge={site.data ? <Badge value={site.data.status} /> : null}
        actions={
          <>
            <Link to={`/conversations?site=${siteId}`} className={buttonClass('secondary')}>
              <Icon name="chat" className="size-4" /> {text('Συνομιλίες', 'Conversations')}
            </Link>
            <Link to={`/contacts?site=${siteId}`} className={buttonClass('secondary')}>
              <Icon name="mail" className="size-4" /> {text('Επαφές', 'Contacts')}
            </Link>
            {props.canWrite ? (
              <PreviewButton siteId={siteId} onOpen={() => setPreviewOpen(true)} />
            ) : null}
          </>
        }
      />
      {site.error ? <ErrorNote message={site.error.message} /> : null}
      <Tabs
        items={tabItems}
        value={section}
        onChange={(key) => {
          const next = new URLSearchParams(params);
          if (key === 'general') next.delete('section');
          else next.set('section', key);
          setParams(next);
        }}
      />
      {section === 'appearance' ? (
        <Panel
          loading={!bot.data && !bot.error}
          error={bot.error?.message ?? null}
          empty={!bot.data}
          emptyLabel={text('Δεν υπάρχει bot.', 'No bot is configured.')}
        >
          {bot.data ? (
            props.canWrite ? (
              <BotForm siteId={siteId} bot={bot.data} onSaved={() => void bot.mutate()} />
            ) : (
              <ReadOnlyAppearance bot={bot.data} />
            )
          ) : null}
        </Panel>
      ) : null}
      {section === 'instructions' && bot.data ? (
        <InstructionsPanel
          key={`${bot.data.name}:${bot.data.settings.businessInstructions ?? ''}`}
          siteId={siteId}
          bot={bot.data}
          canWrite={props.canWrite}
          onSaved={() => void bot.mutate()}
        />
      ) : null}
      {section === 'danger' && props.canWrite ? (
        site.data ? (
          <SiteDeleteCard
            siteId={siteId}
            siteName={site.data.name}
            disabled={site.data.status === 'DISABLED'}
            connected={site.data.connection === 'connected'}
            syncedPages={
              sources.data?.filter(
                (row) =>
                  (row.type === 'WP_PAGE' || row.type === 'WP_POST') && row.status !== 'DELETED',
              ).length ?? 0
            }
          />
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
            {text('Φόρτωση…', 'Loading…')}
          </p>
        )
      ) : null}
      {section === 'general' ? (
        <>
          <ActivationCard
            siteId={siteId}
            canWrite={props.canWrite}
            site={site.data}
            sources={sources.data}
            syncs={syncs.data}
            onSiteChanged={() => void site.mutate()}
            onSyncRequested={() => void syncs.mutate()}
          />

          <Card
            title={text('Πηγές γνώσης', 'Knowledge sources')}
            description={text(
              'Περιεχόμενο που συγχρονίστηκε από το WordPress. Μόνο ενεργές εκδόσεις απαντούν.',
              'Content synced from WordPress. Only active versions are used in answers.',
            )}
            actions={
              <Link to={`/knowledge?site=${siteId}`} className={buttonClass('secondary')}>
                <Icon name="file" className="size-4" /> {text('Βάση γνώσης', 'Knowledge base')}
              </Link>
            }
            flush
          >
            {(sources.data ?? []).length === 0 ? (
              <div className="px-5 py-4">
                <Panel
                  loading={!sources.data && !sources.error}
                  error={sources.error?.message ?? null}
                  empty
                  emptyLabel={text('Δεν υπάρχουν πηγές.', 'There are no sources.')}
                  emptyHint={text(
                    'Εκτελέστε συγχρονισμό από το πρόσθετο WordPress.',
                    'Run a sync from the WordPress plugin.',
                  )}
                >
                  {null}
                </Panel>
              </div>
            ) : (
              <PagedTableShell pagination={sourcePaging.pager}>
                <Table
                  head={[
                    text('Τίτλος', 'Title'),
                    text('Τύπος', 'Type'),
                    text('Γλώσσα', 'Language'),
                    text('Πηγή', 'Source'),
                    text('Έκδοση', 'Version'),
                  ]}
                >
                  {sourcePaging.rows.map((source) => (
                    <tr
                      key={source.id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/70 dark:hover:bg-slate-800/70"
                    >
                      <Td>
                        <Link className={linkClass} to={`/sites/${siteId}/sources/${source.id}`}>
                          {source.title}
                        </Link>
                      </Td>
                      <Td muted>{source.type}</Td>
                      <Td muted>
                        <span className="uppercase">{source.locale ?? '—'}</span>
                      </Td>
                      <Td>
                        <Badge value={source.status} />
                      </Td>
                      <Td>
                        {source.latestVersionStatus ? (
                          <Badge value={source.latestVersionStatus} />
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </Table>
              </PagedTableShell>
            )}
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <OriginsCard siteId={siteId} canWrite={props.canWrite} />
            <SyncRunsCard
              siteId={siteId}
              canWrite={props.canWrite}
              syncs={syncs.data}
              loading={!syncs.data && !syncs.error}
              error={syncs.error?.message ?? null}
              onRefresh={() => {
                void syncs.mutate();
              }}
            />
          </div>
        </>
      ) : null}
      <Card
        title={text('Προεπισκόπηση διαχειριστή', 'Administrator preview')}
        description={text(
          'Το widget φορτώνει σε απομονωμένο iframe με εισιτήριο μίας χρήσης.',
          'The widget loads in an isolated iframe with a single-use ticket.',
        )}
        className={previewOpen ? '' : 'hidden'}
        flush
      >
        <iframe
          id="admin-preview"
          title={text('Προεπισκόπηση', 'Preview')}
          hidden
          className="h-[32rem] w-full rounded-b-2xl bg-slate-50 dark:bg-slate-950"
        />
      </Card>
    </section>
  );
}

function ActivationCard(props: {
  siteId: string;
  canWrite: boolean;
  site:
    | (SiteRow & {
        lastHeartbeatAt?: string | null;
      })
    | undefined;
  sources: Array<{ status: string }> | undefined;
  syncs: Array<{ status: string }> | undefined;
  onSiteChanged: () => void;
  onSyncRequested: () => void;
}) {
  const { text, locale } = useAdminLocale();
  const [pluginError, setPluginError] = useState<string | null>(null);
  const [pluginPending, setPluginPending] = useState(false);
  const [syncPending, setSyncPending] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(
    null,
  );
  const { pluginMenuName } = useUiPrefs();
  const site = props.site;
  const sourceRows = props.sources;
  const syncRows = props.syncs;
  const sourcesKnown = sourceRows !== undefined;
  const syncsKnown = syncRows !== undefined;
  const connected = site?.connection === 'connected';
  const revoked = site?.connection === 'revoked';
  const synced =
    sourcesKnown && syncsKnown
      ? sourceRows.some((row) => row.status !== 'DELETED') ||
        syncRows.some((run) => run.status === 'SUCCEEDED')
      : null;
  const activeSources = sourceRows?.filter((row) => row.status === 'ACTIVE').length ?? 0;
  const approved = sourcesKnown ? activeSources > 0 : null;
  const openIndex =
    !site || !sourcesKnown || !syncsKnown
      ? -2
      : !connected
        ? 0
        : synced !== true
          ? 2
          : approved !== true
            ? 3
            : -1;

  function downloadPlugin(): void {
    setPluginPending(true);
    setPluginError(null);
    void downloadFile(`/v1/sites/${props.siteId}/plugin`, 'unibyte-chat.zip')
      .catch((error: unknown) => {
        setPluginError(
          error instanceof Error
            ? error.message
            : text('Η λήψη του πρόσθετου απέτυχε', 'Plugin download failed'),
        );
      })
      .finally(() => setPluginPending(false));
  }

  function requestSync(): void {
    setSyncPending(true);
    setSyncMessage(null);
    void readJson(`/v1/sites/${props.siteId}/sync-requests`, { method: 'POST' })
      .then(() => {
        setSyncMessage({
          tone: 'ok',
          text: text(
            'Το αίτημα καταχωρήθηκε. Ο συγχρονισμός γίνεται αμέσως από το WordPress, στο Περιεχόμενο.',
            'The request was submitted. WordPress will sync immediately from Content.',
          ),
        });
        props.onSyncRequested();
      })
      .catch((error: unknown) => {
        setSyncMessage({
          tone: 'error',
          text:
            error instanceof Error
              ? error.message
              : text('Το αίτημα συγχρονισμού απέτυχε', 'The sync request failed'),
        });
      })
      .finally(() => setSyncPending(false));
  }

  const steps: Array<{ title: string; body: string; done: boolean | null; extra?: ReactNode }> = [
    {
      title: text('Κατεβάστε το πρόσθετο', 'Download the plugin'),
      body: text(
        'Στο WordPress ανοίξτε Πρόσθετα → Νέο πρόσθετο → Μεταφόρτωση, ανεβάστε το αρχείο και ενεργοποιήστε το.',
        'In WordPress, open Plugins → Add New Plugin → Upload Plugin, upload the file, and activate it.',
      ),
      done: site ? connected : null,
      extra:
        openIndex === 0 ? (
          <Button type="button" disabled={pluginPending} onClick={downloadPlugin}>
            <Icon name="download" className="size-4" />
            {pluginPending
              ? text('Λήψη…', 'Downloading…')
              : text('Λήψη πρόσθετου', 'Download plugin')}
          </Button>
        ) : null,
    },
    {
      title: text('Συνδέστε το WordPress', 'Connect WordPress'),
      body: revoked
        ? text(
            `Η προηγούμενη σύνδεση ανακλήθηκε. Εκδώστε νέο κωδικό και επικολλήστε τον στο ${pluginMenuName} → Σύνδεση. Το website πρέπει να είναι ${site?.canonicalOrigin ?? ''}.`,
            `The previous connection was revoked. Issue a new code and paste it in ${pluginMenuName} → Connection. The website must be ${site?.canonicalOrigin ?? ''}.`,
          )
        : text(
            `Εκδώστε κωδικό και επικολλήστε τον στο WordPress, μενού ${pluginMenuName} → Σύνδεση. Ισχύει 10 λεπτά και χρησιμοποιείται μία φορά. Το website πρέπει να είναι ${site?.canonicalOrigin ?? ''}.`,
            `Issue a code and paste it in WordPress under ${pluginMenuName} → Connection. It is valid for 10 minutes and can be used once. The website must be ${site?.canonicalOrigin ?? ''}.`,
          ),
      done: site ? connected : null,
      extra: openIndex === 0 && props.canWrite ? <PairingActions siteId={props.siteId} /> : null,
    },
    {
      title: text('Στείλτε τις σελίδες', 'Send the pages'),
      body: text(
        `Στο WordPress, ${pluginMenuName} → Περιεχόμενο, επιλέξτε τις δημοσιευμένες σελίδες και πατήστε Συγχρονισμός τώρα.`,
        `In WordPress, open ${pluginMenuName} → Content, select the published pages, and click Sync now.`,
      ),
      done: synced,
      extra:
        openIndex === 2 && props.canWrite && connected ? (
          <Button type="button" variant="secondary" disabled={syncPending} onClick={requestSync}>
            <Icon name="refresh" className="size-4" /> {text('Αίτημα συγχρονισμού', 'Request sync')}
          </Button>
        ) : null,
    },
    {
      title: text('Εγκρίνετε τις σελίδες', 'Approve the pages'),
      body:
        (sourceRows?.length ?? 0) > 0
          ? text(
              'Οι σελίδες έφτασαν πρόχειρες. Ανοίξτε τη βάση γνώσης και πατήστε Ενεργοποίηση. Μόνο οι ενεργές σελίδες απαντούν.',
              'The pages arrived as drafts. Open the knowledge base and click Activate. Only active pages are used in answers.',
            )
          : text(
              'Μόλις φτάσουν οι σελίδες, μένουν πρόχειρες. Ανοίξτε τη βάση γνώσης και πατήστε Ενεργοποίηση.',
              'When pages arrive, they remain drafts. Open the knowledge base and click Activate.',
            ),
      done: approved,
      extra:
        openIndex === 3 ? (
          <Link to={`/knowledge?site=${props.siteId}`} className={buttonClass('secondary')}>
            <Icon name="file" className="size-4" /> {text('Βάση γνώσης', 'Knowledge base')}
          </Link>
        ) : null,
    },
  ];

  return (
    <Card
      title={text('Ενεργοποίηση', 'Activation')}
      description={
        site?.status === 'DISABLED'
          ? text(
              'Η ιστοσελίδα είναι ανενεργή. Χωρίς ενεργοποίηση το πρόσθετο δεν συνδέεται και το chatbot δεν απαντά.',
              'The website is inactive. Until it is activated, the plugin cannot connect and the chatbot will not answer.',
            )
          : openIndex === -1
            ? text(
                'Το chatbot απαντά από τις εγκεκριμένες σελίδες.',
                'The chatbot answers using approved pages.',
              )
            : text(
                'Με αυτή τη σειρά το chatbot αρχίζει να απαντά από τις σελίδες του website.',
                'Follow these steps to make the chatbot answer using the website pages.',
              )
      }
      actions={
        props.canWrite && site ? (
          <Button
            type="button"
            variant={site.status === 'ACTIVE' ? 'danger' : 'primary'}
            onClick={() => {
              const next = site.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
              void readJson(`/v1/sites/${props.siteId}`, {
                method: 'PATCH',
                body: JSON.stringify({ status: next }),
              }).then(() => props.onSiteChanged());
            }}
          >
            <Icon name={site.status === 'ACTIVE' ? 'ban' : 'check'} className="size-4" />
            {site.status === 'ACTIVE'
              ? text('Απενεργοποίηση', 'Deactivate')
              : text('Ενεργοποίηση', 'Activate')}
          </Button>
        ) : null
      }
    >
      {!site ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
          {text('Φόρτωση…', 'Loading…')}
        </p>
      ) : (
        <ol className="space-y-4">
          {steps.map((step, index) => {
            const tone =
              step.done === true
                ? 'done'
                : openIndex === 0 && index <= 1
                  ? 'current'
                  : index === openIndex
                    ? 'current'
                    : 'later';
            return (
              <li key={step.title} className="flex gap-3">
                <span
                  className={
                    tone === 'done'
                      ? 'grid size-7 shrink-0 place-items-center rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                      : tone === 'current'
                        ? 'grid size-7 shrink-0 place-items-center rounded-full bg-blue-600 text-xs font-semibold text-white'
                        : 'grid size-7 shrink-0 place-items-center rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold text-slate-500 dark:text-slate-400 dark:text-slate-500'
                  }
                >
                  {tone === 'done' ? <Icon name="check" className="size-3.5" /> : index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={
                      tone === 'later'
                        ? 'text-sm font-medium text-slate-500 dark:text-slate-400 dark:text-slate-500'
                        : 'text-sm font-medium text-slate-900 dark:text-slate-100'
                    }
                  >
                    {step.title}
                  </p>
                  {tone === 'current' ? (
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
                      {step.body}
                    </p>
                  ) : null}
                  {tone === 'done' && index === 1 ? (
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
                      {text('Συνδεδεμένο · τελευταία επικοινωνία', 'Connected · last contact')}{' '}
                      {formatTime(site.lastHeartbeatAt, locale)}
                    </p>
                  ) : null}
                  {tone === 'done' && index === 3 && activeSources ? (
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
                      {activeSources === 1
                        ? text('1 σελίδα ενεργή.', '1 active page.')
                        : text(
                            `${activeSources} σελίδες ενεργές.`,
                            `${activeSources} active pages.`,
                          )}
                    </p>
                  ) : null}
                  {tone === 'current' && step.extra ? (
                    <div className="mt-3">{step.extra}</div>
                  ) : null}
                </div>
              </li>
            );
          })}
          <li className="flex gap-3">
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold text-slate-500 dark:text-slate-400 dark:text-slate-500">
              5
            </span>
            <div className="min-w-0 flex-1">
              <p
                className={
                  openIndex === -1
                    ? 'text-sm font-medium text-slate-900 dark:text-slate-100'
                    : 'text-sm font-medium text-slate-500 dark:text-slate-400 dark:text-slate-500'
                }
              >
                {text('Δείξτε το chatbot στους επισκέπτες', 'Show the chatbot to visitors')}
              </p>
              {openIndex === -1 ? (
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
                  {text(
                    `Στο WordPress, ${pluginMenuName} → Έλεγχος λειτουργίας, ο διακόπτης «Εμφάνιση του chatbot στους επισκέπτες» δείχνει ή κρύβει το κουμπί.`,
                    `In WordPress, under ${pluginMenuName} → Operation check, the “Show chatbot to visitors” switch shows or hides the button.`,
                  )}
                </p>
              ) : null}
            </div>
          </li>
        </ol>
      )}
      {pluginError ? (
        <div className="mt-4">
          <ErrorNote message={pluginError} />
        </div>
      ) : null}
      {syncMessage && openIndex === 2 ? (
        <p
          className={`mt-3 text-sm ${syncMessage.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}
        >
          {syncMessage.text}
        </p>
      ) : null}
      {connected ? (
        <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 dark:border-slate-800 pt-4">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={pluginPending}
              onClick={downloadPlugin}
            >
              <Icon name="download" className="size-4" />
              {pluginPending
                ? text('Λήψη…', 'Downloading…')
                : text('Λήψη πρόσθετου', 'Download plugin')}
            </Button>
            {props.canWrite && synced ? (
              <Button
                type="button"
                variant="secondary"
                disabled={syncPending}
                onClick={requestSync}
              >
                <Icon name="refresh" className="size-4" />{' '}
                {text('Αίτημα συγχρονισμού', 'Request sync')}
              </Button>
            ) : null}
          </div>
          {props.canWrite ? <PairingActions siteId={props.siteId} /> : null}
          {syncMessage && openIndex !== 2 ? (
            <p
              className={`text-sm ${syncMessage.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}
            >
              {syncMessage.text}
            </p>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function siteDeleteError(message: string, text: (el: string, en: string) => string): string {
  if (message.includes('Disable the site')) {
    return text('Απενεργοποιήστε πρώτα την ιστοσελίδα.', 'Deactivate the website first.');
  }
  if (message.includes('Revoke the WordPress')) {
    return text('Αποσυνδέστε πρώτα το WordPress.', 'Disconnect WordPress first.');
  }
  if (message.includes('Remove synced WordPress')) {
    return text(
      'Αφαιρέστε πρώτα τις συγχρονισμένες σελίδες WordPress.',
      'Remove the synced WordPress pages first.',
    );
  }
  return message;
}

function SiteDeleteCard(props: {
  siteId: string;
  siteName: string;
  disabled: boolean;
  connected: boolean;
  syncedPages: number;
}) {
  const { text } = useAdminLocale();
  const navigate = useNavigate();
  const confirmDialog = useConfirm();
  const [confirmName, setConfirmName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const blockers: string[] = [];
  if (!props.disabled) {
    blockers.push(
      text(
        'Απενεργοποιήστε πρώτα την ιστοσελίδα από την καρτέλα Γενικά.',
        'First deactivate the website from the General tab.',
      ),
    );
  }
  if (props.connected) {
    blockers.push(
      text(
        'Αποσυνδέστε πρώτα το WordPress (ανάκληση σύνδεσης).',
        'Disconnect WordPress first (revoke the connection).',
      ),
    );
  }
  if (props.syncedPages > 0) {
    blockers.push(
      props.syncedPages === 1
        ? text(
            'Υπάρχει 1 συγχρονισμένη σελίδα WordPress. Αφαιρέστε την από τη βάση γνώσης.',
            'There is 1 synced WordPress page. Remove it from the knowledge base.',
          )
        : text(
            `Υπάρχουν ${props.syncedPages} συγχρονισμένες σελίδες WordPress. Αφαιρέστε τις από τη βάση γνώσης.`,
            `There are ${props.syncedPages} synced WordPress pages. Remove them from the knowledge base.`,
          ),
    );
  }
  const canDelete = blockers.length === 0 && confirmName.trim() === props.siteName;

  return (
    <Card
      title={text('Διαγραφή ιστοσελίδας', 'Delete website')}
      description={text(
        'Οριστική διαγραφή όλων των δεδομένων της ιστοσελίδας. Επιτρέπεται μόνο όταν είναι ανενεργή, χωρίς σύνδεση WordPress και χωρίς συγχρονισμένες σελίδες.',
        'Permanently delete all website data. This is allowed only when the website is inactive, disconnected from WordPress, and has no synced pages.',
      )}
    >
      {blockers.length > 0 ? (
        <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-amber-900 dark:text-amber-200">
          {blockers.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400 dark:text-slate-500">
          {text(
            'Θα διαγραφούν bot, πηγές, συνομιλίες, ενδιαφέροντα, λογότυπο και ρυθμίσεις. Η ενέργεια δεν αναιρείται.',
            'The bot, sources, conversations, leads, logo, and settings will be deleted. This action cannot be undone.',
          )}
        </p>
      )}
      <Field
        label={text(
          'Πληκτρολογήστε το όνομα της ιστοσελίδας για επιβεβαίωση',
          'Type the website name to confirm',
        )}
      >
        <TextInput
          value={confirmName}
          onChange={(event) => setConfirmName(event.target.value)}
          placeholder={props.siteName}
          disabled={pending || blockers.length > 0}
        />
      </Field>
      {error ? <p className="mt-3 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
      <div className="mt-4">
        <Button
          type="button"
          variant="danger"
          disabled={!canDelete || pending}
          onClick={() => {
            void (async () => {
              const confirmed = await confirmDialog({
                title: text('Οριστική διαγραφή', 'Permanently delete'),
                message: text(
                  `Να διαγραφεί οριστικά η ιστοσελίδα «${props.siteName}»;\n\nΘα σβηστούν όλα τα δεδομένα της και η ενέργεια δεν αναιρείται.`,
                  `Permanently delete the website “${props.siteName}”?\n\nAll of its data will be erased and this action cannot be undone.`,
                ),
                confirmLabel: text('Διαγραφή', 'Delete'),
                danger: true,
              });
              if (!confirmed) return;
              setPending(true);
              setError(null);
              try {
                const response = await apiRequest(`/v1/sites/${props.siteId}`, {
                  method: 'DELETE',
                });
                if (!response.ok) {
                  const raw = await readErrorMessage(
                    response,
                    text('Η διαγραφή απέτυχε', 'Deletion failed'),
                  );
                  throw new Error(siteDeleteError(raw, text));
                }
                navigate('/sites');
              } catch (caught: unknown) {
                setError(
                  caught instanceof Error
                    ? caught.message
                    : text('Η διαγραφή απέτυχε', 'Deletion failed'),
                );
              } finally {
                setPending(false);
              }
            })();
          }}
        >
          <Icon name="trash" className="size-4" />
          {pending
            ? text('Διαγραφή…', 'Deleting…')
            : text('Οριστική διαγραφή', 'Permanently delete')}
        </Button>
      </div>
    </Card>
  );
}

function PairingActions(props: { siteId: string }) {
  const { text, locale } = useAdminLocale();
  const [issued, setIssued] = useState<{ code: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState(false);

  async function issue(): Promise<void> {
    setPending(true);
    setMessage(null);
    setCopied(false);
    try {
      const result = await readJson<{ code: string; expiresAt: string }>(
        `/v1/sites/${props.siteId}/pairing-codes`,
        { method: 'POST' },
      );
      setIssued(result);
    } catch (error) {
      setMessage({
        tone: 'error',
        text:
          error instanceof Error
            ? error.message
            : text('Η έκδοση κωδικού απέτυχε', 'Issuing the code failed'),
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <div>
      {issued ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/40 p-3">
          <p className="text-xs font-medium text-blue-700 dark:text-blue-300">
            {text('Εμφανίζεται μία φορά', 'Shown only once')}
          </p>
          <code className="mt-1 block text-sm break-all text-slate-900 dark:text-slate-100 select-all">
            {issued.code}
          </code>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600 dark:text-slate-400 dark:text-slate-500">
            <span>
              {text('λήγει', 'expires')} {formatTime(issued.expiresAt, locale)}
            </span>
            <button
              type="button"
              className="font-medium text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-200"
              onClick={() => {
                void navigator.clipboard?.writeText(issued.code).then(() => setCopied(true));
              }}
            >
              <Icon name={copied ? 'check' : 'copy'} className="mr-1 inline size-3.5" />
              {copied ? text('Αντιγράφηκε', 'Copied') : text('Αντιγραφή', 'Copy')}
            </button>
          </div>
        </div>
      ) : null}
      <div className={issued ? 'mt-3' : ''}>
        <Button type="button" variant="secondary" onClick={() => void issue()} disabled={pending}>
          <Icon name="key" className="size-4" />
          {issued ? text('Νέος κωδικός', 'New code') : text('Έκδοση κωδικού', 'Issue code')}
        </Button>
      </div>
      {message ? (
        <p
          className={`mt-3 text-sm ${message.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}
        >
          {message.text}
        </p>
      ) : null}
    </div>
  );
}

function SyncRunsCard(props: {
  siteId: string;
  canWrite: boolean;
  loading: boolean;
  error: string | null;
  syncs:
    | Array<{
        id: string;
        status: string;
        finishedAt: string | null;
        createdAt?: string;
        errorCode: string | null;
        counters?: { accepted: number; rejected: number; duplicate: number; total: number };
        progress?: { done: number; total: number; percent: number };
        failedItems?: Array<{
          id: string;
          externalId: string;
          revision: number;
          status: string;
          errorCode: string | null;
        }>;
      }>
    | undefined;
  onRefresh: () => void;
}) {
  const { text, locale } = useAdminLocale();
  const [syncPending, setSyncPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const latest = props.syncs?.[0];
  const lastLabel = latest?.finishedAt
    ? formatTime(latest.finishedAt, locale)
    : latest?.createdAt
      ? formatTime(latest.createdAt, locale)
      : '—';

  async function requestSync(): Promise<void> {
    setSyncPending(true);
    setMessage(null);
    try {
      await readJson(`/v1/sites/${props.siteId}/sync-requests`, { method: 'POST' });
      setMessage(
        text(
          'Το αίτημα καταχωρήθηκε. Το plugin θα συγχρονίσει στο επόμενο heartbeat.',
          'The request was submitted. The plugin will sync on its next heartbeat.',
        ),
      );
      props.onRefresh();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : text('Το αίτημα απέτυχε', 'The request failed'),
      );
    } finally {
      setSyncPending(false);
    }
  }

  return (
    <Card
      title={text('Συγχρονισμοί', 'Syncs')}
      description={text(
        'Πρόοδος, αποτυχίες και ιστορικό από το WordPress.',
        'Progress, failures, and history from WordPress.',
      )}
      actions={
        props.canWrite ? (
          <Button
            type="button"
            variant="secondary"
            disabled={syncPending}
            onClick={() => void requestSync()}
          >
            <Icon name="refresh" className="size-4" />
            {syncPending
              ? text('Αίτημα…', 'Requesting…')
              : text('Αίτημα συγχρονισμού', 'Request sync')}
          </Button>
        ) : null
      }
    >
      <dl className="mb-4 grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs text-slate-500">{text('Τελευταίος συγχρονισμός', 'Last sync')}</dt>
          <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">{lastLabel}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">
            {text('Επόμενος προγραμματισμένος', 'Next scheduled')}
          </dt>
          <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
            {text(
              'Ορίζεται στο WordPress (ωριαία / ημερήσια)',
              'Configured in WordPress (hourly / daily)',
            )}
          </dd>
        </div>
      </dl>
      {message ? (
        <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">{message}</p>
      ) : null}
      <Panel
        loading={props.loading}
        error={props.error}
        empty={(props.syncs ?? []).length === 0}
        emptyLabel={text('Δεν υπάρχει συγχρονισμός.', 'There are no sync runs.')}
      >
        <ol className="space-y-3">
          {(props.syncs ?? []).map((run) => {
            const percent = run.progress?.percent ?? 0;
            const failed = run.failedItems ?? [];
            return (
              <li
                key={run.id}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-slate-700 dark:text-slate-300">
                      {formatTime(run.finishedAt ?? run.createdAt ?? null, locale)}
                    </p>
                    {run.counters ? (
                      <p className="mt-0.5 text-xs text-slate-500">
                        {run.counters.accepted} {text('αποδεκτά', 'accepted')} ·{' '}
                        {run.counters.rejected} {text('απορρίφθηκαν', 'rejected')} ·{' '}
                        {run.counters.duplicate} {text('διπλότυπα', 'duplicates')}
                      </p>
                    ) : null}
                    {run.errorCode ? (
                      <p className="mt-0.5 text-xs text-rose-700 dark:text-rose-300">
                        {run.errorCode}
                      </p>
                    ) : null}
                  </div>
                  <Badge value={run.status} />
                </div>
                {run.progress && run.progress.total > 0 ? (
                  <div className="mt-2">
                    <div
                      className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"
                      role="progressbar"
                      aria-valuenow={percent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="h-full rounded-full bg-blue-600"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {run.progress.done}/{run.progress.total} ({percent}%)
                    </p>
                  </div>
                ) : null}
                {failed.length > 0 ? (
                  <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2 dark:border-slate-800">
                    <li className="text-xs font-medium text-rose-700 dark:text-rose-300">
                      {text('Αποτυχημένα στοιχεία', 'Failed items')}
                    </li>
                    {failed.map((item) => (
                      <li
                        key={item.id}
                        className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600 dark:text-slate-300"
                      >
                        <span className="truncate font-mono">
                          {item.externalId}
                          {item.errorCode ? ` · ${item.errorCode}` : ''}
                        </span>
                        {props.canWrite ? (
                          <span className="flex gap-2">
                            <button
                              type="button"
                              className="font-medium text-blue-600 hover:text-blue-800"
                              onClick={() => void requestSync()}
                            >
                              {text('Επανάληψη', 'Retry')}
                            </button>
                            <span
                              className="text-slate-400"
                              title={text(
                                'Η παράβλεψη γίνεται στην ουρά του plugin',
                                'Items are skipped in the plugin queue',
                              )}
                            >
                              {text('Παράβλεψη στο WP', 'Skip in WP')}
                            </span>
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ol>
      </Panel>
    </Card>
  );
}

function OriginsCard(props: { siteId: string; canWrite: boolean }) {
  const { text } = useAdminLocale();
  const origins = useRemote<Array<{ id: string; exactOrigin: string; environment: string }>>(
    `/v1/sites/${props.siteId}/origins`,
  );
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const rows = origins.data ?? [];
  return (
    <Card
      title={text('Εγκεκριμένα origins', 'Approved origins')}
      description={text(
        'Μόνο αυτά τα origins μπορούν να φορτώσουν το widget. Το αρχικό δεν διαγράφεται.',
        'Only these origins can load the widget. The original origin cannot be deleted.',
      )}
    >
      {origins.error ? (
        <p className="text-sm text-rose-700 dark:text-rose-300">{origins.error.message}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row, index) => (
            <li
              key={row.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm"
            >
              <span className="min-w-0 truncate text-slate-800 dark:text-slate-200">
                {row.exactOrigin}
              </span>
              {props.canWrite && index > 0 ? (
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-rose-700 dark:text-rose-300 hover:text-rose-900"
                  onClick={() => {
                    setError(null);
                    void readJson(`/v1/sites/${props.siteId}/origins/${row.id}`, {
                      method: 'DELETE',
                    })
                      .then(() => origins.mutate())
                      .catch((caught: unknown) => {
                        setError(
                          caught instanceof Error
                            ? caught.message
                            : text('Η διαγραφή απέτυχε', 'Deletion failed'),
                        );
                      });
                  }}
                >
                  <Icon name="trash" className="mr-1 inline size-3.5" />{' '}
                  {text('Αφαίρεση', 'Remove')}
                </button>
              ) : (
                <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
                  {index === 0 ? 'canonical' : row.environment.toLowerCase()}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {props.canWrite ? (
        <form
          className="mt-3 flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            void readJson(`/v1/sites/${props.siteId}/origins`, {
              method: 'POST',
              body: JSON.stringify({ exactOrigin: value }),
            })
              .then(() => {
                setValue('');
                return origins.mutate();
              })
              .catch((caught: unknown) => {
                setError(
                  caught instanceof Error
                    ? caught.message
                    : text('Η προσθήκη απέτυχε', 'Addition failed'),
                );
              });
          }}
        >
          <TextInput
            flush
            type="url"
            required
            placeholder="https://www.example.gr"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            aria-label={text('Νέο origin', 'New origin')}
          />
          <Button
            type="submit"
            variant="secondary"
            aria-label={text('Προσθήκη origin', 'Add origin')}
            title={text('Προσθήκη', 'Add')}
          >
            <Icon name="plus" className="size-4" />
          </Button>
        </form>
      ) : null}
      {error ? <p className="mt-2 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
    </Card>
  );
}

const BOT_LOCALES = ['el', 'en', 'fr'] as const;
function localeLabel(
  code: (typeof BOT_LOCALES)[number],
  text: (el: string, en: string) => string,
): string {
  return {
    el: text('Ελληνικά', 'Greek'),
    en: text('Αγγλικά', 'English'),
    fr: text('Γαλλικά', 'French'),
  }[code];
}

function activeLocaleCodes(bot: BotData): Array<(typeof BOT_LOCALES)[number]> {
  return BOT_LOCALES.filter((code) => {
    const row = bot.locales.find((locale) => locale.locale === code);
    return row?.enabled === true || code === bot.defaultLocale;
  });
}

function ReadOnlyAppearance(props: { bot: BotData }) {
  const { text } = useAdminLocale();
  const look = appearanceFrom(props.bot);
  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
      <Card
        title={text('Εμφάνιση chatbot', 'Chatbot appearance')}
        description={text(
          'Όνομα, χαιρετισμοί, χρώμα και θέση. Η επεξεργασία θέλει ρόλο διαχειριστή.',
          'Name, greetings, color, and position. Editing requires an administrator role.',
        )}
        actions={
          <Badge
            value={props.bot.enabled ? 'ACTIVE' : 'DISABLED'}
            label={props.bot.enabled ? text('Ενεργό', 'Active') : text('Ανενεργό', 'Inactive')}
          />
        }
      >
        <dl className="grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {text('Όνομα', 'Name')}
            </dt>
            <dd className="mt-1 font-medium text-slate-900 dark:text-slate-100">
              {props.bot.name}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {text('Χρώμα', 'Color')}
            </dt>
            <dd className="mt-1 flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
              <span className="size-4 rounded" style={{ background: look.color }} />
              {look.color}
            </dd>
          </div>
        </dl>
        <ul className="mt-4 grid gap-2">
          {activeLocaleCodes(props.bot).map((code) => {
            const locale = props.bot.locales.find((row) => row.locale === code);
            return (
              <li
                key={code}
                className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm"
              >
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {localeLabel(code, text)}
                </span>
                <p className="mt-1 text-slate-600 dark:text-slate-400 dark:text-slate-500">
                  {locale?.greeting ?? ''}
                </p>
              </li>
            );
          })}
        </ul>
      </Card>
      <Card title={text('Προεπισκόπηση chatbot', 'Chatbot preview')}>
        <ChatPreview
          title={look.name}
          greeting={look.greeting}
          color={look.color}
          position={look.position}
          locales={look.locales}
          privacyUrl={props.bot.settings.privacyUrl}
          contactUrl={props.bot.settings.contactUrl}
          leadNotifyEmail={props.bot.settings.leadNotifyEmail}
          starterQuestions={
            props.bot.locales.find((row) => row.locale === props.bot.defaultLocale)
              ?.starterQuestions
          }
        />
      </Card>
    </div>
  );
}

function BotForm(props: { siteId: string; bot: BotData; onSaved: () => void }) {
  const { text } = useAdminLocale();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [pending, setPending] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoAssetId, setLogoAssetId] = useState<string | null>(
    props.bot.settings.logoAssetId ?? null,
  );
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const logoUrlRef = useRef<string | null>(null);
  const stored = new Map(props.bot.locales.map((locale) => [locale.locale, locale]));
  const [name, setName] = useState(props.bot.name);
  const [color, setColor] = useState(primaryColor(props.bot.settings));
  const [position, setPosition] = useState<'left' | 'right'>(
    props.bot.settings.position === 'left' ? 'left' : 'right',
  );
  const [defaultLocale, setDefaultLocale] = useState(props.bot.defaultLocale);
  const [privacyUrl, setPrivacyUrl] = useState(props.bot.settings.privacyUrl ?? '');
  const [contactUrl, setContactUrl] = useState(props.bot.settings.contactUrl ?? '');
  const [leadNotifyEmail, setLeadNotifyEmail] = useState(props.bot.settings.leadNotifyEmail ?? '');
  const active = activeLocaleCodes(props.bot);
  const [greetings, setGreetings] = useState<Record<string, string>>(() =>
    Object.fromEntries(active.map((code) => [code, stored.get(code)?.greeting ?? ''])),
  );
  const [starterQuestions, setStarterQuestions] = useState<Record<string, StarterQuestion[]>>(() =>
    Object.fromEntries(
      active.map((code) => [
        code,
        (stored.get(code)?.starterQuestions ?? []).map((item) => ({
          question: item.question,
          answer: item.answer ?? '',
          icon: item.icon ?? 'QUESTION',
          action: item.action ?? 'ANSWER',
        })),
      ]),
    ),
  );
  const [draggedStarter, setDraggedStarter] = useState<{ code: string; index: number } | null>(
    null,
  );
  const previewLocales = active;

  useEffect(() => {
    setLogoAssetId(props.bot.settings.logoAssetId ?? null);
  }, [props.bot.settings.logoAssetId]);

  useEffect(() => {
    let cancelled = false;
    function replaceLogoUrl(next: string | null): void {
      if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
      logoUrlRef.current = next;
      setLogoUrl(next);
    }
    if (!logoAssetId) {
      replaceLogoUrl(null);
      return () => {
        cancelled = true;
      };
    }
    void loadAssetObjectUrl(`/v1/sites/${props.siteId}/assets/${logoAssetId}/download`).then(
      (url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) replaceLogoUrl(url);
      },
    );
    return () => {
      cancelled = true;
      if (logoUrlRef.current) {
        URL.revokeObjectURL(logoUrlRef.current);
        logoUrlRef.current = null;
      }
    };
  }, [logoAssetId, props.siteId]);

  return (
    <form
      className="admin-page"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const locales = active.flatMap((code) => {
          const greeting = String(form.get(`greeting-${code}`) ?? '').trim();
          const fallback = String(form.get(`fallback-${code}`) ?? '').trim();
          if (!greeting || !fallback) return [];
          return [
            {
              locale: code,
              enabled: true,
              greeting,
              fallback,
              starterQuestions: (starterQuestions[code] ?? [])
                .map((item) => ({
                  question: item.question.trim(),
                  answer: item.answer.trim(),
                  icon: item.icon,
                  action: item.action,
                }))
                .filter((item) => item.question && (item.action !== 'ANSWER' || item.answer)),
            },
          ];
        });
        setSaveError(null);
        setSaved(false);
        setPending(true);
        void readJson(`/v1/sites/${props.siteId}/bot`, {
          method: 'PATCH',
          body: JSON.stringify({
            name: name.trim(),
            enabled: form.get('enabled') === 'on',
            defaultLocale,
            locales,
            settings: {
              privacyUrl: privacyUrl.trim() || null,
              contactUrl: contactUrl.trim() || null,
              leadNotifyEmail: leadNotifyEmail.trim() || null,
              noticeVersion: String(form.get('noticeVersion') ?? props.bot.settings.noticeVersion),
              colors: { primary: color },
              position,
            },
          }),
        })
          .then(() => {
            setSaved(true);
            props.onSaved();
          })
          .catch((error: unknown) => {
            setSaveError(
              error instanceof Error ? error.message : text('Αποτυχία αποθήκευσης', 'Save failed'),
            );
          })
          .finally(() => setPending(false));
      }}
    >
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <Card
          title={text('Εμφάνιση chatbot', 'Chatbot appearance')}
          description={text(
            'Όνομα, χαιρετισμός, χρώμα και θέση. Φαίνονται μόνο οι γλώσσες που έχουν προστεθεί στις ρυθμίσεις.',
            'Name, greeting, color, and position. Only languages added in settings are shown.',
          )}
          actions={<Badge value={props.bot.enabled ? 'ACTIVE' : 'DISABLED'} />}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={text('Όνομα βοηθού', 'Assistant name')}
              hint={text('Εμφανίζεται στην κεφαλίδα του chatbot.', 'Shown in the chatbot header.')}
            >
              <TextInput
                name="name"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
            <Field
              label={text('Κύριο χρώμα', 'Primary color')}
              hint={text('Κεφαλίδα και κουμπί αποστολής.', 'Header and send button.')}
            >
              <ColorField value={color} onChange={setColor} />
            </Field>
            <Field
              label={text('Θέση', 'Position')}
              hint={text(
                'Σε ποιο σημείο της σελίδας εμφανίζεται ο launcher.',
                'Where the launcher appears on the page.',
              )}
            >
              <Select
                className="mt-1.5"
                name="position"
                value={position}
                onChange={(next) => setPosition(next === 'left' ? 'left' : 'right')}
                options={[
                  { value: 'right', label: text('Κάτω δεξιά', 'Bottom right') },
                  { value: 'left', label: text('Κάτω αριστερά', 'Bottom left') },
                ]}
              />
            </Field>
            <Field
              label={text('Λογότυπο', 'Logo')}
              hint={text(
                'PNG, JPEG ή WebP έως 512 KiB. Εμφανίζεται στην κεφαλίδα του widget.',
                'PNG, JPEG, or WebP up to 512 KiB. Shown in the widget header.',
              )}
            >
              <div className="mt-1.5 space-y-2">
                {logoUrl ? (
                  <div className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-3 py-2">
                    <img
                      src={logoUrl}
                      alt={text('Τρέχον λογότυπο', 'Current logo')}
                      className="size-10 rounded-full bg-white dark:bg-slate-900 object-cover ring-1 ring-slate-200"
                    />
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      {logoBusy
                        ? text('Ανέβασμα…', 'Uploading…')
                        : text(
                            'Ανεβασμένο — εμφανίζεται στην προεπισκόπηση.',
                            'Uploaded — shown in the preview.',
                          )}
                    </p>
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <FileField
                    accept="image/png,image/jpeg,image/webp"
                    disabled={logoBusy}
                    label={text('Επιλογή λογότυπου', 'Choose logo')}
                    aria-label={text('Ανέβασμα λογότυπου', 'Upload logo')}
                    onChange={(file) => {
                      if (!file) return;
                      if (file.size > 512 * 1024) {
                        setSaveError(
                          text('Το λογότυπο ξεπερνά τα 512 KiB.', 'The logo exceeds 512 KiB.'),
                        );
                        return;
                      }
                      setLogoBusy(true);
                      setSaveError(null);
                      const localPreview = URL.createObjectURL(file);
                      if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
                      logoUrlRef.current = localPreview;
                      setLogoUrl(localPreview);
                      void apiRequest(`/v1/sites/${props.siteId}/logo`, {
                        method: 'POST',
                        body: file,
                        headers: { 'Content-Type': file.type || 'application/octet-stream' },
                      })
                        .then(async (response) => {
                          if (!response.ok) {
                            throw new Error(
                              await readErrorMessage(
                                response,
                                text('Αποτυχία ανεβάσματος λογότυπου', 'Logo upload failed'),
                              ),
                            );
                          }
                          const body = (await response.json()) as { assetId?: string };
                          if (!body.assetId) {
                            throw new Error(
                              text('Αποτυχία ανεβάσματος λογότυπου', 'Logo upload failed'),
                            );
                          }
                          setLogoAssetId(body.assetId);
                          props.onSaved();
                        })
                        .catch((error: unknown) => {
                          setSaveError(
                            error instanceof Error
                              ? error.message
                              : text('Αποτυχία ανεβάσματος λογότυπου', 'Logo upload failed'),
                          );
                          setLogoAssetId(props.bot.settings.logoAssetId ?? null);
                        })
                        .finally(() => setLogoBusy(false));
                    }}
                  />
                  {logoAssetId ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={logoBusy}
                      onClick={() => {
                        setLogoBusy(true);
                        setSaveError(null);
                        void apiRequest(`/v1/sites/${props.siteId}/logo`, { method: 'DELETE' })
                          .then(async (response) => {
                            if (!response.ok) {
                              throw new Error(
                                await readErrorMessage(
                                  response,
                                  text('Αποτυχία αφαίρεσης λογότυπου', 'Logo removal failed'),
                                ),
                              );
                            }
                            setLogoAssetId(null);
                            props.onSaved();
                          })
                          .catch((error: unknown) => {
                            setSaveError(
                              error instanceof Error
                                ? error.message
                                : text('Αποτυχία αφαίρεσης λογότυπου', 'Logo removal failed'),
                            );
                          })
                          .finally(() => setLogoBusy(false));
                      }}
                    >
                      <Icon name="trash" className="size-4" /> {text('Αφαίρεση', 'Remove')}
                    </Button>
                  ) : null}
                </div>
              </div>
            </Field>
            <Field label={text('Προεπιλεγμένη γλώσσα', 'Default language')}>
              <Select
                className="mt-1.5"
                name="defaultLocale"
                value={defaultLocale}
                onChange={setDefaultLocale}
                options={active.map((code) => ({
                  value: code,
                  label: localeLabel(code, text),
                }))}
              />
            </Field>
          </div>

          <Checkbox
            className="mt-4"
            boxed
            name="enabled"
            defaultChecked={props.bot.enabled}
            label={text('Το bot είναι ενεργό στην ιστοσελίδα', 'The bot is active on the website')}
          />

          <div className="mt-4 grid gap-4">
            {active.map((code) => {
              const locale = stored.get(code);
              return (
                <fieldset
                  key={code}
                  className="rounded-[var(--admin-radius)] border border-[color:var(--admin-border)] bg-[color:var(--admin-paper)] px-4 pt-2 pb-4 dark:bg-slate-950/60"
                >
                  <legend className="px-1 text-xs font-semibold tracking-wider text-slate-500 dark:text-slate-400 dark:text-slate-500 uppercase">
                    {localeLabel(code, text)}
                  </legend>
                  <div className="mt-3 grid gap-3">
                    <Field
                      label={text('Χαιρετισμός', 'Greeting')}
                      hint={text(
                        'Το πρώτο μήνυμα που βλέπει ο επισκέπτης.',
                        'The first message visitors see.',
                      )}
                    >
                      <TextInput
                        name={`greeting-${code}`}
                        value={greetings[code] ?? ''}
                        onChange={(event) =>
                          setGreetings((current) => ({ ...current, [code]: event.target.value }))
                        }
                      />
                    </Field>
                    <Field label={text('Εφεδρικό μήνυμα', 'Fallback message')}>
                      <Textarea
                        name={`fallback-${code}`}
                        rows={3}
                        defaultValue={locale?.fallback ?? ''}
                      />
                    </Field>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/60">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {text('Αρχικές ερωτήσεις', 'Starter questions')}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {text(
                              'Προαιρετικές επιλογές που εμφανίζονται πριν από το πρώτο μήνυμα. Η έτοιμη απάντηση εμφανίζεται χωρίς κλήση AI ή χρέωση.',
                              'Optional choices shown before the first message. The prepared answer is displayed without an AI call or charge.',
                            )}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={(starterQuestions[code]?.length ?? 0) >= 6}
                          onClick={() =>
                            setStarterQuestions((current) => ({
                              ...current,
                              [code]: [
                                ...(current[code] ?? []),
                                {
                                  question: '',
                                  answer: '',
                                  icon: 'QUESTION',
                                  action: 'ANSWER',
                                },
                              ],
                            }))
                          }
                        >
                          <Icon name="plus" className="size-4" /> {text('Προσθήκη', 'Add')}
                        </Button>
                      </div>
                      {(starterQuestions[code] ?? []).length > 0 ? (
                        <div className="mt-3 grid gap-3">
                          {(starterQuestions[code] ?? []).map((item, index) => (
                            <div
                              key={`${code}-${index}`}
                              onDragOver={(event) => {
                                if (draggedStarter?.code === code) event.preventDefault();
                              }}
                              onDrop={(event) => {
                                event.preventDefault();
                                if (
                                  !draggedStarter ||
                                  draggedStarter.code !== code ||
                                  draggedStarter.index === index
                                ) {
                                  return;
                                }
                                setStarterQuestions((current) => {
                                  const rows = [...(current[code] ?? [])];
                                  const [moved] = rows.splice(draggedStarter.index, 1);
                                  if (!moved) return current;
                                  rows.splice(index, 0, moved);
                                  return { ...current, [code]: rows };
                                });
                                setDraggedStarter(null);
                              }}
                              className={`rounded-lg border bg-white p-3 dark:bg-slate-950 ${
                                draggedStarter?.code === code && draggedStarter.index === index
                                  ? 'border-blue-400 opacity-60 dark:border-blue-500'
                                  : 'border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              <div className="mb-3 flex items-center justify-between gap-3">
                                <span
                                  draggable
                                  onDragStart={(event) => {
                                    event.dataTransfer.effectAllowed = 'move';
                                    setDraggedStarter({ code, index });
                                  }}
                                  onDragEnd={() => setDraggedStarter(null)}
                                  className="cursor-grab select-none text-xs font-semibold text-slate-500 active:cursor-grabbing dark:text-slate-400"
                                  title={text('Σύρετε για αλλαγή σειράς', 'Drag to change order')}
                                >
                                  ⋮⋮ {index + 1}
                                </span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() =>
                                    setStarterQuestions((current) => ({
                                      ...current,
                                      [code]: (current[code] ?? []).filter(
                                        (_, rowIndex) => rowIndex !== index,
                                      ),
                                    }))
                                  }
                                >
                                  <Icon name="trash" className="size-4" />{' '}
                                  {text('Αφαίρεση', 'Remove')}
                                </Button>
                              </div>
                              <div className="grid gap-3">
                                <Field label={`${text('Ερώτηση', 'Question')} ${index + 1}`}>
                                  <TextInput
                                    required
                                    maxLength={160}
                                    value={item.question}
                                    onChange={(event) =>
                                      setStarterQuestions((current) => ({
                                        ...current,
                                        [code]: (current[code] ?? []).map((row, rowIndex) =>
                                          rowIndex === index
                                            ? { ...row, question: event.target.value }
                                            : row,
                                        ),
                                      }))
                                    }
                                  />
                                </Field>
                                <div className="grid gap-3 sm:grid-cols-2">
                                  <Field label={text('Εικονίδιο', 'Icon')}>
                                    <Select
                                      value={item.icon}
                                      onChange={(value) =>
                                        setStarterQuestions((current) => ({
                                          ...current,
                                          [code]: (current[code] ?? []).map((row, rowIndex) =>
                                            rowIndex === index
                                              ? { ...row, icon: value as StarterIcon }
                                              : row,
                                          ),
                                        }))
                                      }
                                      options={[
                                        {
                                          value: 'QUESTION',
                                          label: text('Ερώτηση', 'Question'),
                                          icon: <Icon name="help" className="size-4" />,
                                        },
                                        {
                                          value: 'INFO',
                                          label: text('Πληροφορίες', 'Information'),
                                          icon: <Icon name="info" className="size-4" />,
                                        },
                                        {
                                          value: 'SERVICES',
                                          label: text('Υπηρεσίες', 'Services'),
                                          icon: <Icon name="building" className="size-4" />,
                                        },
                                        {
                                          value: 'PRICE',
                                          label: text('Τιμή', 'Price'),
                                          icon: <Icon name="coins" className="size-4" />,
                                        },
                                        {
                                          value: 'CLOCK',
                                          label: text('Ωράριο', 'Hours'),
                                          icon: <Icon name="clock" className="size-4" />,
                                        },
                                        {
                                          value: 'CONTACT',
                                          label: text('Επικοινωνία', 'Contact'),
                                          icon: <Icon name="mail" className="size-4" />,
                                        },
                                      ]}
                                    />
                                  </Field>
                                  <Field label={text('Ενέργεια', 'Action')}>
                                    <Select
                                      value={item.action}
                                      onChange={(value) =>
                                        setStarterQuestions((current) => ({
                                          ...current,
                                          [code]: (current[code] ?? []).map((row, rowIndex) =>
                                            rowIndex === index
                                              ? { ...row, action: value as StarterAction }
                                              : row,
                                          ),
                                        }))
                                      }
                                      options={[
                                        {
                                          value: 'ANSWER',
                                          label: text('Έτοιμη απάντηση', 'Prepared answer'),
                                          icon: <Icon name="chat" className="size-4" />,
                                        },
                                        {
                                          value: 'CONTACT',
                                          label: text('Επικοινωνία', 'Contact'),
                                          icon: <Icon name="mail" className="size-4" />,
                                        },
                                        {
                                          value: 'QUOTE',
                                          label: text('Ζητήστε προσφορά', 'Request a quote'),
                                          icon: <Icon name="coins" className="size-4" />,
                                        },
                                      ]}
                                    />
                                  </Field>
                                </div>
                                {item.action === 'ANSWER' ? (
                                  <Field label={text('Έτοιμη απάντηση', 'Prepared answer')}>
                                    <Textarea
                                      required
                                      maxLength={2000}
                                      rows={3}
                                      value={item.answer}
                                      onChange={(event) =>
                                        setStarterQuestions((current) => ({
                                          ...current,
                                          [code]: (current[code] ?? []).map((row, rowIndex) =>
                                            rowIndex === index
                                              ? { ...row, answer: event.target.value }
                                              : row,
                                          ),
                                        }))
                                      }
                                    />
                                  </Field>
                                ) : (
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {item.action === 'CONTACT'
                                      ? text(
                                          'Ανοίγει τη φόρμα επικοινωνίας.',
                                          'Opens the contact form.',
                                        )
                                      : text(
                                          'Ανοίγει τη φόρμα με έτοιμο μήνυμα αιτήματος προσφοράς.',
                                          'Opens the form with a prepared quote-request message.',
                                        )}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                          {text(
                            'Δεν έχουν οριστεί αρχικές ερωτήσεις για αυτή τη γλώσσα.',
                            'No starter questions are configured for this language.',
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </fieldset>
              );
            })}
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Field label="Privacy URL">
              <TextInput
                name="privacyUrl"
                type="url"
                value={privacyUrl}
                onChange={(event) => setPrivacyUrl(event.target.value)}
                placeholder="https://"
              />
            </Field>
            <Field label="Contact URL">
              <TextInput
                name="contactUrl"
                type="url"
                value={contactUrl}
                onChange={(event) => setContactUrl(event.target.value)}
                placeholder="https://"
              />
            </Field>
            <Field
              label={text('Email ειδοποίησης ενδιαφερόντων', 'Lead notification email')}
              hint={text(
                'Νέα διεύθυνση ενεργοποιείται αφού ανοίξει τον σύνδεσμο επιβεβαίωσης.',
                'A new address is activated after opening the confirmation link.',
              )}
            >
              <TextInput
                name="leadNotifyEmail"
                type="email"
                value={leadNotifyEmail}
                onChange={(event) => setLeadNotifyEmail(event.target.value)}
              />
              {props.bot.settings.leadNotifyEmailPending ? (
                <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                  {text('Εκκρεμεί επιβεβαίωση για', 'Confirmation pending for')}{' '}
                  {props.bot.settings.leadNotifyEmailPending}.
                </p>
              ) : null}
            </Field>
            <Field
              label={text('Έκδοση ενημέρωσης απορρήτου', 'Privacy notice version')}
              hint={text('Αποθηκεύεται μαζί με κάθε ενδιαφέρον.', 'Stored with each lead.')}
            >
              <TextInput name="noticeVersion" defaultValue={props.bot.settings.noticeVersion} />
            </Field>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={pending}>
              <Icon name="save" className="size-4" />
              {pending ? text('Αποθήκευση…', 'Saving…') : text('Αποθήκευση', 'Save')}
            </Button>
            {saved ? (
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                {text('Αποθηκεύτηκε.', 'Saved.')}
              </p>
            ) : null}
            {saveError ? (
              <p className="text-sm text-rose-700 dark:text-rose-300">{saveError}</p>
            ) : null}
          </div>
        </Card>
        <Card
          title={text('Προεπισκόπηση chatbot', 'Chatbot preview')}
          description={text(
            'Δείχνει όνομα, χρώμα, χαιρετισμό, απορρήτου/επικοινωνίας και email ειδοποίησης. Δεν καλεί το μοντέλο.',
            'Shows the name, color, greeting, privacy/contact links, and notification email. It does not call the model.',
          )}
          className="h-full"
        >
          <ChatPreview
            title={name}
            greeting={greetings[defaultLocale] ?? ''}
            color={color}
            position={position}
            locales={previewLocales}
            logoUrl={logoUrl}
            privacyUrl={privacyUrl}
            contactUrl={contactUrl}
            leadNotifyEmail={leadNotifyEmail}
            starterQuestions={starterQuestions[defaultLocale] ?? []}
          />
        </Card>
      </div>
    </form>
  );
}

function PreviewButton(props: { siteId: string; onOpen: () => void }) {
  const { text, locale } = useAdminLocale();
  const [error, setError] = useState<string | null>(null);
  const attempt = useRef(0);
  const readyListener = useRef<((event: MessageEvent) => void) | null>(null);
  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        onClick={() => {
          const current = ++attempt.current;
          if (readyListener.current) {
            window.removeEventListener('message', readyListener.current);
            readyListener.current = null;
          }
          setError(null);
          void readJson<{ ticket: string; widgetOrigin: string; config: unknown }>(
            `/v1/sites/${props.siteId}/preview-sessions`,
            { method: 'POST' },
          )
            .then((preview) => {
              if (current !== attempt.current) return;
              const frame = document.getElementById('admin-preview') as HTMLIFrameElement | null;
              if (!frame) return;
              const parent = window.location.origin;
              props.onOpen();
              frame.hidden = false;
              let sent = false;
              const send = () => {
                if (sent || current !== attempt.current || !frame.contentWindow) return;
                sent = true;
                window.removeEventListener('message', onReady);
                if (readyListener.current === onReady) readyListener.current = null;
                frame.contentWindow.postMessage(
                  {
                    type: 'unibyte-chat:bootstrap',
                    ticket: preview.ticket,
                    config: preview.config,
                  },
                  preview.widgetOrigin,
                );
              };
              const onReady = (event: MessageEvent) => {
                if (event.origin !== preview.widgetOrigin || event.source !== frame.contentWindow)
                  return;
                const data = event.data as { type?: string } | null;
                if (data?.type !== 'unibyte-chat:ready') return;
                send();
              };
              readyListener.current = onReady;
              window.addEventListener('message', onReady);
              frame.addEventListener('load', send, { once: true });
              frame.src = `${preview.widgetOrigin}/?parent=${encodeURIComponent(parent)}&lang=${locale}`;
              frame.scrollIntoView({ behavior: 'smooth', block: 'start' });
            })
            .catch((caught: unknown) => {
              if (current !== attempt.current) return;
              setError(
                caught instanceof Error
                  ? caught.message
                  : text('Η προεπισκόπηση απέτυχε', 'Preview failed'),
              );
            });
        }}
      >
        <Icon name="eye" className="size-4" /> {text('Δοκιμή απαντήσεων', 'Test answers')}
      </Button>
      {error ? <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p> : null}
    </div>
  );
}

export function SourceDetailPage(props: { canWrite: boolean }) {
  const { text } = useAdminLocale();
  const { siteId = '', sourceId = '' } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const source = useRemote<{
    id: string;
    type: string;
    title: string;
    status: string;
    activeVersionId: string | null;
    canDelete: boolean;
    canAiExtract: boolean;
    preview: { text: string; status: string; revision: number } | null;
  }>(`/v1/sites/${siteId}/sources/${sourceId}`);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const isPdf = source.data?.type === 'PDF' || source.data?.type === 'WP_PDF';
  const isReady = Boolean(source.data?.activeVersionId && source.data.preview?.status === 'READY');

  useEffect(() => {
    if (!actionsOpen) return;
    function closeActions(event: MouseEvent): void {
      if (!actionsRef.current?.contains(event.target as Node)) setActionsOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') setActionsOpen(false);
    }
    document.addEventListener('mousedown', closeActions);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeActions);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [actionsOpen]);

  async function act(action: 'activate' | 'reject'): Promise<void> {
    const approved = await confirm({
      title:
        action === 'activate'
          ? text('Ενεργοποίηση πηγής', 'Activate source')
          : text('Απόρριψη πηγής', 'Reject source'),
      message:
        action === 'activate'
          ? text(
              `Να ενεργοποιηθεί η πηγή «${source.data?.title ?? ''}» και να χρησιμοποιείται στις απαντήσεις του chatbot;`,
              `Activate the source “${source.data?.title ?? ''}” and use it in chatbot answers?`,
            )
          : text(
              `Να απορριφθεί η πηγή «${source.data?.title ?? ''}»;\n\nΔεν θα χρησιμοποιείται πλέον στις απαντήσεις του chatbot.`,
              `Reject the source “${source.data?.title ?? ''}”?\n\nIt will no longer be used in chatbot answers.`,
            ),
      confirmLabel:
        action === 'activate' ? text('Ενεργοποίηση', 'Activate') : text('Απόρριψη', 'Reject'),
      danger: action === 'reject',
    });
    if (!approved) return;
    setActionError(null);
    setPending(true);
    try {
      await readJson(`/v1/sites/${siteId}/sources/${sourceId}/${action}`, { method: 'POST' });
      await source.mutate();
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : text('Η ενέργεια απέτυχε', 'The action failed'),
      );
    } finally {
      setPending(false);
    }
  }

  async function openPdf(): Promise<void> {
    setActionError(null);
    setPending(true);
    try {
      await openAuthenticatedPdf(`/v1/sites/${siteId}/sources/${sourceId}/file`);
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : text('Το PDF δεν μπόρεσε να ανοίξει.', 'The PDF could not be opened.'),
      );
    } finally {
      setPending(false);
    }
  }

  async function runAiExtract(): Promise<void> {
    const approved = await confirm({
      title: text('Εξαγωγή κειμένου με AI', 'Extract text with AI'),
      message: text(
        `Να εξαχθεί ξανά το κείμενο της πηγής «${source.data?.title ?? ''}» με AI όραση;\n\nΗ ενέργεια χρησιμοποιεί τον πάροχο AI της πλατφόρμας και χρεώνεται στο budget. Το τοπικό OCR παραμένει το αυτόματο fallback.`,
        `Extract the text from “${source.data?.title ?? ''}” again using AI vision?\n\nThis action uses the platform AI provider and is charged to the budget. Local OCR remains the automatic fallback.`,
      ),
      confirmLabel: text('Εξαγωγή με AI', 'Extract with AI'),
    });
    if (!approved) return;
    setActionError(null);
    setPending(true);
    try {
      await readJson(`/v1/sites/${siteId}/sources/${sourceId}/ai-extract`, { method: 'POST' });
      await source.mutate();
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : text('Η εξαγωγή με AI απέτυχε', 'AI extraction failed'),
      );
    } finally {
      setPending(false);
    }
  }

  async function deleteSource(): Promise<void> {
    const approved = await confirm({
      title: text('Διαγραφή πηγής', 'Delete source'),
      message: text(
        `Να διαγραφεί οριστικά η πηγή «${source.data?.title ?? ''}»;\n\nΘα αφαιρεθούν το κείμενο, τα chunks και το αποθηκευμένο αρχείο όπου υπάρχει.`,
        `Permanently delete the source “${source.data?.title ?? ''}”?\n\nIts text, chunks, and stored file, where present, will be removed.`,
      ),
      confirmLabel: text('Διαγραφή', 'Delete'),
      danger: true,
    });
    if (!approved) return;
    setActionError(null);
    setPending(true);
    try {
      await readJson(`/v1/sites/${siteId}/sources/${sourceId}`, { method: 'DELETE' });
      navigate(`/knowledge?site=${siteId}`);
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : text('Η διαγραφή απέτυχε', 'Deletion failed'),
      );
      setPending(false);
    }
  }

  return (
    <section className="admin-page">
      <PageHeader
        title={source.data?.title ?? text('Πηγή', 'Source')}
        description={text(
          'Έλεγχος της τελευταίας έκδοσης πριν τη χρήση της σε απαντήσεις.',
          'Review the latest version before it is used in answers.',
        )}
        actions={
          source.data ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {isPdf ? (
                <Button type="button" disabled={pending} onClick={() => void openPdf()}>
                  <Icon name="file" className="size-4" /> {text('Άνοιγμα PDF', 'Open PDF')}
                </Button>
              ) : null}
              {props.canWrite && source.data.canAiExtract ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={pending}
                  onClick={() => void runAiExtract()}
                >
                  <Icon name="sparkles" className="size-4" /> {text('Εξαγωγή AI', 'AI extraction')}
                </Button>
              ) : null}
              {props.canWrite && !isReady ? (
                <Button type="button" disabled={pending} onClick={() => void act('activate')}>
                  <Icon name="check" className="size-4" /> {text('Ενεργοποίηση', 'Activate')}
                </Button>
              ) : null}
              {props.canWrite && (source.data.status !== 'DISABLED' || source.data.canDelete) ? (
                <div ref={actionsRef} className="relative">
                  <button
                    type="button"
                    aria-label={text('Περισσότερες ενέργειες', 'More actions')}
                    aria-haspopup="menu"
                    aria-expanded={actionsOpen}
                    disabled={pending}
                    onClick={() => setActionsOpen((open) => !open)}
                    className="grid size-9 place-items-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--admin-accent)]/40 disabled:opacity-45 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                  >
                    <Icon name="more" className="size-4" />
                  </button>
                  {actionsOpen ? (
                    <div
                      role="menu"
                      className="absolute top-11 right-0 z-20 w-40 overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                    >
                      {source.data.status !== 'DISABLED' ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setActionsOpen(false);
                            void act('reject');
                          }}
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40"
                        >
                          <Icon name="x" className="size-4" /> {text('Απόρριψη', 'Reject')}
                        </button>
                      ) : null}
                      {source.data.canDelete ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setActionsOpen(false);
                            void deleteSource();
                          }}
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40"
                        >
                          <Icon name="trash" className="size-4" /> {text('Διαγραφή', 'Delete')}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null
        }
      />
      <p className="text-sm">
        <Link
          className={`${linkClass} inline-flex items-center gap-1`}
          to={`/knowledge?site=${siteId}`}
        >
          <Icon name="arrow" className="size-4 rotate-180" />{' '}
          {text('Πίσω στη Βάση γνώσης', 'Back to Knowledge base')}
        </Link>
      </p>
      {actionError ? (
        <p className="text-sm text-rose-700 dark:text-rose-300">{actionError}</p>
      ) : null}
      <Panel
        loading={!source.data && !source.error}
        error={source.error?.message ?? null}
        empty={!source.data?.preview}
        emptyLabel={text('Δεν υπάρχει προεπισκόπηση.', 'No preview is available.')}
        emptyHint={text(
          'Η πηγή δεν έχει ακόμη επεξεργασμένη έκδοση.',
          'The source does not have a processed version yet.',
        )}
      >
        <Card
          title={text('Προεπισκόπηση κειμένου', 'Text preview')}
          actions={
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
              <Badge value={source.data?.status} />
              <span>revision {source.data?.preview?.revision}</span>
              <Badge value={source.data?.preview?.status} />
            </div>
          }
          flush
        >
          <pre className="max-h-[36rem] overflow-auto px-5 py-4 text-sm leading-6 whitespace-pre-wrap text-slate-800 dark:text-slate-200">
            {source.data?.preview?.text}
          </pre>
        </Card>
      </Panel>
    </section>
  );
}
